import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  GitMerge,
  XCircle,
  CheckCircle2,
  GitBranch,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import DataTable from "../components/ui/DataTable";
import BranchFormModal from "../components/BranchFormModal";
import { branchService } from "../services/branchService";
import { baselineService } from "../services/baselineService";
import { getStoredClientId } from "../utils/tokenUtils";
import toast from "react-hot-toast";
import { logger } from "../services/logService";

const BranchManagementPage = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id");
  const clientId = searchParams.get("client_id") || getStoredClientId();
  const baselineId = searchParams.get("baseline_id");

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);

  const loadBranches = useCallback(async () => {
    if (!projectId || !clientId) return;

    try {
      setLoading(true);
      const filters = {};
      if (statusFilter !== "all") {
        filters.status = statusFilter;
      }
      const response = await branchService.getBranches(
        projectId,
        clientId,
        baselineId,
        filters,
      );

      // Handle different response structures
      let branchesList = [];

      // Check for structure: { success: true, message: { message: "...", data: [...] }, data: null }
      if (response?.message?.data && Array.isArray(response.message.data)) {
        branchesList = response.message.data;
      }
      // Check for structure: { data: [...] }
      else if (response?.data) {
        if (Array.isArray(response.data)) {
          branchesList = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          branchesList = response.data.data;
        } else if (
          response.data?.branches &&
          Array.isArray(response.data.branches)
        ) {
          branchesList = response.data.branches;
        }
      }
      // Check if response is directly an array
      else if (Array.isArray(response)) {
        branchesList = response;
      }

      console.log("Branch response:", response); // Debug log
      console.log("Loaded branches:", branchesList); // Debug log
      setBranches(branchesList);
    } catch (error) {
      logger.error(error, "Failed to load branches");
      toast.error("Failed to load branches");
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, clientId, baselineId, statusFilter]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const handleCreateBranch = () => {
    setEditingBranch(null);
    setShowBranchModal(true);
  };

  const handleEditBranch = (branch) => {
    if (branch.status !== "active") {
      toast.error(
        `Cannot edit ${branch.status} branch. Only active branches can be edited.`,
      );
      return;
    }
    setEditingBranch(branch);
    setShowBranchModal(true);
  };

  const handleMergeBranch = async (branch) => {
    if (
      !window.confirm(
        `Are you sure you want to merge "${branch.name}" into its baseline?`,
      )
    ) {
      return;
    }

    try {
      await branchService.mergeBranch(branch.branch_id, projectId, clientId);
      toast.success("Branch merged successfully!");
      loadBranches();
    } catch (error) {
      logger.error(error, "Failed to merge branch");
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to merge branch";
      toast.error(errorMessage);
    }
  };

  const handleCloseBranch = async (branch) => {
    if (!window.confirm(`Are you sure you want to close "${branch.name}"?`)) {
      return;
    }

    try {
      await branchService.closeBranch(branch.branch_id, projectId, clientId);
      toast.success("Branch closed successfully!");
      loadBranches();
    } catch (error) {
      logger.error(error, "Failed to close branch");
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to close branch";
      toast.error(errorMessage);
    }
  };

  const handleDeleteBranch = async (branch) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${branch.name}"? This will fail if the branch has active test runs.`,
      )
    ) {
      return;
    }

    try {
      await branchService.deleteBranch(branch.branch_id, projectId, clientId);
      toast.success("Branch deleted successfully!");
      loadBranches();
    } catch (error) {
      logger.error(error, "Failed to delete branch");
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete branch";
      toast.error(errorMessage);
      if (error.response?.data?.code === "BRANCH_HAS_ACTIVE_RUNS") {
        toast.error("Cannot delete branch with active test runs.");
      }
    }
  };

  const filteredBranches = branches.filter(
    (branch) =>
      branch.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      branch.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      branch.baseline_name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const columns = [
    {
      header: "Branch Name",
      field: "name",
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
        </div>
      ),
    },
    {
      header: "Baseline",
      field: "baseline_name",
      render: (value, row) => (
        <div>
          <span className="text-gray-600">{row.baseline_name || "-"}</span>
          {row.baseline_version && (
            <span className="text-gray-500 text-sm ml-2">
              v{row.baseline_version}
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Status",
      field: "status",
      render: (value) => {
        const status = value || "active";
        const statusColors = {
          active: "bg-green-100 text-green-800",
          merged: "bg-blue-100 text-blue-800",
          closed: "bg-gray-100 text-gray-800",
        };
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || "bg-gray-100 text-gray-800"}`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      },
    },
    {
      header: "Description",
      field: "description",
      render: (value) => <span className="text-gray-600">{value || "-"}</span>,
    },
    {
      header: "Test Cases",
      field: "test_cases_count",
      render: (value) => <span className="text-gray-600">{value || 0}</span>,
    },
    {
      header: "Actions",
      field: "branch_id",
      render: (value, row) => {
        const isActive = row.status === "active";

        return (
          <div className="flex items-center gap-2">
            {isActive && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditBranch(row)}
                  title="Edit branch"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleMergeBranch(row)}
                  title="Merge branch"
                  className="text-blue-600 hover:text-blue-700"
                >
                  <GitMerge className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCloseBranch(row)}
                  title="Close branch"
                  className="text-gray-600 hover:text-gray-700"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteBranch(row)}
              title="Delete branch"
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  if (!projectId) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Please select a project to manage branches.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Branches</h1>
            <p className="text-gray-600 mt-1">
              Manage branches for BASELINE_ENABLED structure projects
              {baselineId && " (filtered by baseline)"}
            </p>
          </div>
          <Button onClick={handleCreateBranch}>
            <Plus className="w-4 h-4 mr-2" />
            Create Branch
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <Input
            placeholder="Search branches..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="merged">Merged</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm">
        <DataTable
          columns={columns}
          data={filteredBranches}
          loading={loading}
          emptyStateContent="No branches found. Create your first branch from a published baseline."
        />
      </div>

      {/* Branch Form Modal */}
      <BranchFormModal
        isOpen={showBranchModal}
        onClose={() => {
          setShowBranchModal(false);
          setEditingBranch(null);
        }}
        onSubmit={loadBranches}
        initialData={editingBranch}
        projectId={projectId}
        clientId={clientId}
        baselineId={baselineId}
      />
    </div>
  );
};

export default BranchManagementPage;
